import React, { useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";

const TRIGGERS = [
  { id: "equipment_added", label: "Equipment added (e.g. pump P-301A)",
    sample: { tag: "P-301A", type: "pump", service: "centrifugal", design_T_C: 60, design_P_barg: 8 } },
  { id: "line_drawn", label: "Line drawn (e.g. column bottoms)",
    sample: { id: "L-401-001", from: "T-401", to: "TK-401", size_in: 6, service: "column bottoms" } },
  { id: "vessel_added", label: "Vessel added",
    sample: { tag: "V-501", type: "horizontal_vessel", design_P_barg: 10, design_T_C: 80 } },
  { id: "valve_added", label: "Valve added",
    sample: { tag: "BV-101", type: "ball_valve", size_in: 4 } },
];

export default function AutoCompletePanel({ projectId }) {
  const [trigger, setTrigger] = useState(TRIGGERS[0].id);
  const [target, setTarget] = useState(JSON.stringify(TRIGGERS[0].sample, null, 2));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const onTriggerChange = (id) => {
    setTrigger(id);
    const t = TRIGGERS.find((x) => x.id === id);
    if (t) setTarget(JSON.stringify(t.sample, null, 2));
    setResult(null);
  };

  const suggest = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      let parsedTarget;
      try { parsedTarget = JSON.parse(target); } catch (e) { throw new Error("Invalid JSON in target"); }
      // ── File 53: Pull project context first for few-shot ──
      let context = null;
      try {
        const cx = await api.get(`/projects/${projectId}/ai/autocomplete-context`);
        context = cx.data;
      } catch { /* non-fatal */ }
      const { data } = await api.post(`/projects/${projectId}/ai/autocomplete`,
        { trigger, target: parsedTarget, context });
      setResult({ ...data, _context: context });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e?.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="ac-panel" style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 12 }}>
      <div style={card}>
        <div style={{ ...label, marginBottom: 6 }}>User Action / Trigger</div>
        <select data-testid="ac-trigger"
          style={{ width: "100%", padding: "6px 8px", fontSize: 12, borderRadius: 4,
                   border: "1px solid var(--pf-border)", background: "var(--pf-surface-2)", color: "var(--pf-fg)" }}
          value={trigger} onChange={(e) => onTriggerChange(e.target.value)}>
          {TRIGGERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>

        <div style={{ height: 10 }} />
        <div style={{ ...label, marginBottom: 6 }}>Target Object (JSON)</div>
        <textarea data-testid="ac-target"
          value={target} onChange={(e) => setTarget(e.target.value)}
          style={{ width: "100%", minHeight: 200, padding: 8, fontSize: 11, fontFamily: "monospace",
                   background: "var(--pf-surface-2)", color: "var(--pf-fg)",
                   border: "1px solid var(--pf-border)", borderRadius: 4, boxSizing: "border-box" }} />

        <div style={{ height: 10 }} />
        <button style={{ ...btn, width: "100%" }} data-testid="ac-suggest" onClick={suggest} disabled={busy}>
          {busy ? "Asking Capexara Process Industry AI…" : "Suggest Next Steps"}
        </button>
        {err && <div style={{ marginTop: 8, padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 11 }}>{err}</div>}
      </div>

      <div style={card}>
        <div style={{ ...label, marginBottom: 6 }}>Suggestions</div>
        {result?._context && (
          <div data-testid="ac-context" style={{
            padding: 8, background: "rgba(22,163,74,0.08)",
            border: "1px solid rgba(22,163,74,0.4)", borderRadius: 4,
            marginBottom: 8, fontSize: 11,
          }}>
            <strong style={{ color: "#16a34a" }}>Project context</strong>{" · "}
            <span style={{ color: "var(--pf-muted)" }}>
              {result._context.deliverables_completed}/{result._context.deliverables_total} deliverables
              {" · "}{result._context.completion_pct}%
              {" · last:"} <span style={{ fontFamily: "monospace", color: "#FFB000" }}>{result._context.last_completed}</span>
              {" · next:"} <span style={{ fontFamily: "monospace", color: "#16a34a" }}>{result._context.next_suggested}</span>
            </span>
            {result._context.few_shot_suggestions?.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {result._context.few_shot_suggestions.map((fs, i) => (
                  <div key={i} data-testid={`ac-fewshot-${i}`} style={{
                    fontSize: 11, padding: "4px 0",
                    color: fs.priority === "HIGH" ? "#FCA5A5" : "#F3F4F6",
                  }}>
                    ▸ <strong>{fs.action}</strong>{" — "}
                    <span style={{ color: "var(--pf-muted)" }}>{fs.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!result && !busy && (
          <div style={{ color: "var(--pf-muted)", fontSize: 12, textAlign: "center", padding: 24 }}>
            Pick a trigger and click "Suggest Next Steps" — Capexara Process Industry AI will recommend the next 3-6 most useful additions.
          </div>
        )}
        {busy && <div style={{ color: "var(--pf-muted)", fontSize: 12, textAlign: "center", padding: 24 }}>Thinking…</div>}
        {result?.suggestions?.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {result.suggestions.map((s, i) => (
              <div key={i} style={{ padding: 10, background: "var(--pf-surface-2)", borderRadius: 4, border: "1px solid var(--pf-border)" }}
                   data-testid={`ac-suggestion-${i}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <strong style={{ fontSize: 13, color: "#F26B1F" }}>{s.label}</strong>
                  <PriorityBadge p={s.priority} />
                </div>
                <div style={{ fontSize: 11, color: "var(--pf-muted)", marginBottom: 6 }}>{s.rationale}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button style={{ ...btnGhost, padding: "3px 8px", fontSize: 10 }}>Apply</button>
                  <button style={{ ...btnGhost, padding: "3px 8px", fontSize: 10 }}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {result?.raw && !result?.suggestions?.length && (
          <pre style={{ padding: 8, background: "var(--pf-surface-2)", borderRadius: 4, fontSize: 10, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>{result.raw}</pre>
        )}
      </div>
    </div>
  );
}

function PriorityBadge({ p }) {
  const c = { high: "#ef4444", medium: "#f59e0b", low: "#84cc16" }[p] || "#888";
  return <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 3, background: c + "30", color: c, fontWeight: 700, textTransform: "uppercase" }}>{p || "—"}</span>;
}
